/**
 * Permission codes the Budgeting screens declare an interest in.
 *
 * They are the dot-strings `internal/budget/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them once
 * here means a renamed permission is a compile error rather than a silently
 * invisible menu item.
 *
 * Two groupings are worth stating, because the screens render them as separate
 * decisions rather than inferring one from another:
 *
 *  - `versionManage`, `versionSubmit` and `versionLock` are three different
 *    people's jobs. Loading a draft, proposing it for approval, and deciding
 *    which version is *in force* are separately permissioned on the server
 *    (`doc.go`: lock and supersede are single-actor operational decisions, not
 *    approval chains), so the version screen shows each action only to the
 *    holder of its own permission.
 *  - the control level (`control-levels/permissions.ts`) is not implied by
 *    `versionManage`. Loading numbers into a budget and deciding whether exceeding
 *    one *blocks a payable* are different authorities, and §8.7's control is only
 *    as trustworthy as that separation.
 */

export const BUDGET_PERMISSIONS = {
  modelRead: 'budget.model.read',
  modelManage: 'budget.model.manage',

  versionRead: 'budget.version.read',
  /** Create a version and edit, import, copy or spread its lines while draft. */
  versionManage: 'budget.version.manage',
  versionSubmit: 'budget.version.submit',
  /** Lock, unlock or supersede — which version is in force. */
  versionLock: 'budget.version.lock',

  /** Revise a forecast, freeze a baseline, and edit a forecast's lines (FT13). */
  forecastManage: 'budget.forecast.manage',
  /** Set the materiality threshold; the server gates PUT /budget-settings on the control-level authority. */
  settingsManage: 'budget.control_level.manage',

  commitmentRead: 'budget.commitment.read',
  varianceRead: 'budget.variance.read',

  /** Read the chart of accounts to offer accounts as budget-line keys. */
  accountRead: 'org.account.read',
  /** Read companies to scope a model, a control level or a report. */
  companyRead: 'org.company.read',
  /** Read the dimension trees behind the cost-centre / profit-centre / branch pickers. */
  unitRead: 'org.unit.read',
  /** Read fiscal periods — the columns of the lines grid and of the variance report. */
  fiscalRead: 'org.fiscal.read',
  /** Drill an actual figure through to the journals behind it (§8.2's chain). */
  glReportRead: 'gl.report.read',
} as const

export type BudgetPermission = (typeof BUDGET_PERMISSIONS)[keyof typeof BUDGET_PERMISSIONS]
